import React, { useEffect, useMemo, useState } from 'react';
import { AICoursesStore } from '../stores/ai-courses.store';
import { useContainerData } from '../../../shared/public/SlHooks/useContainerData';
import { AICoursesService } from '../services/ai-courses.service';
import { Container, LearningExperienceType, AuthService } from '../../../symphony';
import useStyles from 'isomorphic-style-loader/useStyles';
import { LocalizedLink } from '../../../shared/public/LocalizedLink/LocalizedLink';
import { SlAIFooter } from '../../../_aifooter/public/SlAIFooter';
import { i18n } from '../../../symphony';

import s from './AICoursesContainer.scss';
import { AINavbarService } from '../../../navigation/private/services/ai-navbar.service';

export const AICoursesContainer = () => {
  useStyles(s);
  const { init } = Container.take('aicourses', AICoursesService);
  const { courses } = useContainerData('aicourses', AICoursesStore, ['courses']);
  const authService = Container.take('global', AuthService);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const { showNavbar } = Container.take('global', AINavbarService);

  useEffect(() => {
    init();
    setIsLoggedIn(authService.isLoggedIn());
    showNavbar();
  }, []);

  const handleGetStarted = () => {
    console.log('Get Started clicked');
  };

  // Course Card Component
  const CourseCard = ({ course, isEnrolled = false }: { course: any; isEnrolled?: boolean; }) => {
    return (
      <LocalizedLink to={`../courses/${course.alias}`} className="ai-course-card">
        <div className="ai-course-card__header">
          {course.uiConfigurations?.iconUrl && (
            <div className="ai-course-card__icon">
              <img src={course.uiConfigurations.iconUrl} alt={course.name} />
            </div>
          )}
          {isEnrolled && <div className="ai-course-card__status-badge">{i18n.t('ai-course-catalog.enrolled-badge')}</div>}
        </div>
        <div className="ai-course-card__content">
          <h3 className="ai-course-card__title">{course.name}</h3>
          {course.description && (
            <p className="ai-course-card__description">{course.description}</p>
          )}
        </div>
      </LocalizedLink>
    );
  };

  // Filter to show only individual courses and separate by enrollment status
  const { enrolledCourses, availableCourses } = useMemo(() => {
    const individualCourses = courses.filter(course =>
      course.typeId === LearningExperienceType.Course
    );

    if (isLoggedIn) {
      return {
        enrolledCourses: individualCourses.filter(course => course.enrollmentStatusId === 1),
        availableCourses: individualCourses.filter(course => course.enrollmentStatusId === 0)
      };
    } else {
      return {
        enrolledCourses: [],
        availableCourses: individualCourses
      };
    }
  }, [courses, isLoggedIn]);

  if (courses.length === 0) {
    return (
      <div className="ai-courses">
        {/* CTA Section */}
        <section className="ai-courses__cta">
          <div className="ai-courses__container">
            <div className="ai-courses__cta-content">
              <h2 className="ai-courses__cta-title">{i18n.t('ai-course-catalog.coming-soon-title')}</h2>
              <p className="ai-courses__cta-subtitle">
                {i18n.t('ai-course-catalog.coming-soon-description')}
              </p>
              <button
                className="ai-courses__button ai-courses__button--primary ai-courses__button--large"
                onClick={handleGetStarted}
              >
                {i18n.t('ai-course-catalog.get-notified-button')}
              </button>
              <p className="ai-courses__cta-note">{i18n.t('ai-course-catalog.coming-soon-note')}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ai-courses">
      {/* AI Courses Section */}
      {isLoggedIn ? (
        <>
          {/* Enrolled Courses Section */}
          {enrolledCourses.length > 0 && (
            <section className="ai-courses__courses">
              <div className="ai-courses__container">
                <h2 className="ai-courses__section-title">{i18n.t('ai-course-catalog.enrolled-courses-title')}</h2>
                <div className="ai-courses__courses-grid">
                  {enrolledCourses.map(course => (
                    <CourseCard key={course.id} course={course} isEnrolled={true} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Available Courses Section */}
          {availableCourses.length > 0 && (
            <section className="ai-courses__courses">
              <div className="ai-courses__container">
                <h2 className="ai-courses__section-title">{i18n.t('ai-course-catalog.available-courses-title')}</h2>
                <div className="ai-courses__courses-grid">
                  {availableCourses.map(course => (
                    <CourseCard key={course.id} course={course} isEnrolled={false} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      ) : (
        /* All Courses Section for Non-Logged-In Users */
        availableCourses.length > 0 && (
          <section className="ai-courses__courses">
            <div className="ai-courses__container">
              <h2 className="ai-courses__section-title">{i18n.t('ai-course-catalog.courses-title')}</h2>
              <div className="ai-courses__courses-grid">
                {availableCourses.map(course => (
                  <CourseCard key={course.id} course={course} isEnrolled={false} />
                ))}
              </div>
            </div>
          </section>
        )
      )}

      {/* Benefits Section */}
      <section className="ai-courses__benefits">
        <div className="ai-courses__container">
          <div className="ai-courses__benefits-content">
            <div className="ai-courses__benefits-text">
              <h2 className="ai-courses__section-title">{i18n.t('ai-course-catalog.benefits-title')}</h2>
              <div className="ai-courses__benefits-list">
                <div className="ai-courses__benefit-item">
                  <div className="ai-courses__benefit-check">✓</div>
                  <span>{i18n.t('ai-course-catalog.benefit-hands-on')}</span>
                </div>
                <div className="ai-courses__benefit-item">
                  <div className="ai-courses__benefit-check">✓</div>
                  <span>{i18n.t('ai-course-catalog.benefit-no-prerequisites')}</span>
                </div>
                <div className="ai-courses__benefit-item">
                  <div className="ai-courses__benefit-check">✓</div>
                  <span>{i18n.t('ai-course-catalog.benefit-real-world')}</span>
                </div>
                <div className="ai-courses__benefit-item">
                  <div className="ai-courses__benefit-check">✓</div>
                  <span>{i18n.t('ai-course-catalog.benefit-certificate')}</span>
                </div>
              </div>
            </div>
            <div className="ai-courses__benefits-visual">
              <div className="ai-courses__progress-card">
                <div className="ai-courses__progress-header">
                  <h4>{i18n.t('ai-course-catalog.progress-header')}</h4>
                </div>
                <div className="ai-courses__progress-items">
                  <div className="ai-courses__progress-item ai-courses__progress-item--complete">
                    <div className="ai-courses__progress-dot"></div>
                    <span>{i18n.t('ai-course-catalog.progress-basics')}</span>
                    <div className="ai-courses__progress-badge">{i18n.t('ai-course-catalog.progress-completed')}</div>
                  </div>
                  <div className="ai-courses__progress-item ai-courses__progress-item--active">
                    <div className="ai-courses__progress-dot"></div>
                    <span>{i18n.t('ai-course-catalog.progress-engineering')}</span>
                    <div className="ai-courses__progress-badge">{i18n.t('ai-course-catalog.progress-in-progress')}</div>
                  </div>
                  <div className="ai-courses__progress-item">
                    <div className="ai-courses__progress-dot"></div>
                    <span>{i18n.t('ai-course-catalog.progress-applications')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SlAIFooter />
    </div>
  );
};
